import {FC, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import {toast} from "react-toastify";
import * as Sentry from "@sentry/react";
import {useSendLeadContactMutation} from "../../../../api/user/api";
import {useAuthUser} from "../../../../components/UserState/hooks";
import {EMAIL_PATTERN} from "../../../../utils/constants";
import {resolveErrorMessage} from "../../../../utils/errors";

const STORAGE_KEY = "proLeadModal:dismissed";
const SCROLL_THRESHOLD = 2 / 3;

interface Props {
  /**
   * Override the dismiss persistence layer in tests so the spec can assert
   * trigger behaviour without polluting real localStorage.
   */
  storage?: Storage;
}

const ProLeadModal: FC<Props> = ({storage}) => {
  const {t} = useTranslation();
  const authUser = useAuthUser();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [sendLeadContact, {isLoading}] = useSendLeadContactMutation();
  const triggeredRef = useRef(false);

  // Logged-in users don't need the lead-capture modal — they're already in
  // the funnel. Skip the listener entirely so we don't track a dismiss for
  // them.
  const isAuthenticated = !!authUser;

  useEffect(() => {
    if (isAuthenticated) return;

    const store = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    if (!store) return;

    if (store.getItem(STORAGE_KEY) === "1") {
      triggeredRef.current = true;
      return;
    }

    const onScroll = () => {
      if (triggeredRef.current) return;

      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;

      const ratio = (window.scrollY || doc.scrollTop) / total;
      if (ratio >= SCROLL_THRESHOLD) {
        triggeredRef.current = true;
        setOpen(true);
      }
    };

    window.addEventListener("scroll", onScroll, {passive: true});
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [storage, isAuthenticated]);

  const dismiss = () => {
    const store = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    store?.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const onSubmit = async () => {
    setTouched(true);
    if (!EMAIL_PATTERN.test(email)) return;

    try {
      await sendLeadContact({email}).unwrap();
      toast.success(t("messages.LeadSent"));
      dismiss();
    } catch (e: any) {
      const error = e?.data ? e.data : e;
      Sentry.captureException(error);
      toast.error(resolveErrorMessage(error, t("errors.LeadSent")));
    }
  };

  const emailError = touched && !EMAIL_PATTERN.test(email);

  return (
    <Dialog open={open} onClose={dismiss} fullWidth maxWidth="sm" data-testid="pro-lead-modal">
      <DialogTitle>{t("proLead.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText className="pb-3">{t("proLead.body")}</DialogContentText>
        <Box>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label={t("fields.Email")}
            value={email}
            error={emailError}
            helperText={emailError ? t("forms.validationMessages.pattern", {field: t("fields.Email"), value: ""}) : ""}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} disabled={isLoading}>
          {t("proLead.dismiss")}
        </Button>
        <Button onClick={onSubmit} disabled={isLoading || email.length === 0} variant="contained">
          {t("forms.actions.Send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProLeadModal;
